import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function Pricing() {
    const navigate = useNavigate();

    useEffect(() => {
        // Set page title
        document.title = 'Pricing Plans - Family Budget Tracker | 30-Day Free Trial';

        // Create or update meta tags
        const updateMetaTag = (name, content, property = false) => {
            const attribute = property ? 'property' : 'name';
            let meta = document.querySelector(`meta[${attribute}="${name}"]`);
            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute(attribute, name);
                document.head.appendChild(meta);
            }
            meta.setAttribute('content', content);
        };

        // Basic SEO meta tags
        updateMetaTag('description', 'Affordable family budget tracker pricing. Start with a 30-day free trial, then choose from $8/month or $60/year plans. Track expenses, manage budgets, and achieve financial goals together.');
        updateMetaTag('keywords', 'family budget tracker pricing, budget app cost, expense tracker plans, family finance software, budget management pricing, financial planning app, household budget tool');
        updateMetaTag('author', 'Family Budget Tracker');
        updateMetaTag('robots', 'index, follow');
        updateMetaTag('viewport', 'width=device-width, initial-scale=1.0');

        // Open Graph meta tags for social media
        updateMetaTag('og:title', 'Family Budget Tracker Pricing - 30-Day Free Trial Available', true);
        updateMetaTag('og:description', 'Start your family budget journey with our 30-day free trial. Affordable plans starting at $5/month when billed annually. Track expenses, set budgets, and manage family finances together.', true);
        updateMetaTag('og:type', 'website', true);
        updateMetaTag('og:url', window.location.href, true);
        updateMetaTag('og:site_name', 'Family Budget Tracker', true);
        updateMetaTag('og:locale', 'en_US', true);

        // Twitter Card meta tags
        updateMetaTag('twitter:card', 'summary_large_image');
        updateMetaTag('twitter:title', 'Family Budget Tracker Pricing - Free Trial Available');
        updateMetaTag('twitter:description', 'Affordable family budget tracking with 30-day free trial. Plans from $5/month. Track expenses, manage budgets, achieve financial goals together.');

        // Additional SEO meta tags
        updateMetaTag('theme-color', '#ff6b35');
        updateMetaTag('application-name', 'Family Budget Tracker');
        updateMetaTag('apple-mobile-web-app-title', 'Family Budget');
        updateMetaTag('apple-mobile-web-app-capable', 'yes');
        updateMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');

        // Structured data for pricing
        const structuredData = {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Family Budget Tracker",
            "applicationCategory": "FinanceApplication",
            "operatingSystem": "Web Browser",
            "offers": [
                {
                    "@type": "Offer",
                    "name": "Free Trial",
                    "price": "0",
                    "priceCurrency": "USD",
                    "priceValidUntil": "2025-12-31",
                    "availability": "https://schema.org/InStock",
                    "validFor": "P30D",
                    "description": "30-day free trial with full access to all features"
                },
                {
                    "@type": "Offer",
                    "name": "Monthly Plan",
                    "price": "8.00",
                    "priceCurrency": "USD",
                    "billingIncrement": "P1M",
                    "priceValidUntil": "2025-12-31",
                    "availability": "https://schema.org/InStock",
                    "description": "Monthly subscription with all features included"
                },
                {
                    "@type": "Offer",
                    "name": "Annual Plan",
                    "price": "60.00",
                    "priceCurrency": "USD",
                    "billingIncrement": "P1Y",
                    "priceValidUntil": "2025-12-31",
                    "availability": "https://schema.org/InStock",
                    "description": "Annual subscription - save 37% compared to monthly billing"
                }
            ],
            "description": "Family budget tracking application that helps families manage expenses, set budgets, and achieve financial goals together.",
            "featureList": [
                "Expense tracking",
                "Budget management",
                "Category organization",
                "CSV import/export",
                "Family member access",
                "Budget alerts",
                "Financial reports",
                "Secure data storage"
            ],
            "screenshot": window.location.origin + "/pricing",
            "url": window.location.href,
            "sameAs": [
                window.location.origin
            ]
        };

        // Add structured data script
        let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
        if (!structuredDataScript) {
            structuredDataScript = document.createElement('script');
            structuredDataScript.type = 'application/ld+json';
            document.head.appendChild(structuredDataScript);
        }
        structuredDataScript.textContent = JSON.stringify(structuredData);

        // Cleanup function to reset title when component unmounts
        return () => {
            document.title = 'Family Budget Tracker';
        };
    }, []);

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            color: '#f0f0f0',
            padding: '20px'
        }}>
            <div style={{
                maxWidth: '900px',
                margin: '0 auto',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '40px',
                border: '1px solid #404040'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '32px',
                    flexWrap: 'wrap',
                    gap: '16px'
                }}>
                    <h1 style={{
                        color: '#ff6b35',
                        margin: '0',
                        fontSize: '2.5rem',
                        fontWeight: '700'
                    }}>
                        Pricing Plans
                    </h1>
                    <button
                        onClick={() => navigate('/login?mode=register')}
                        style={{
                            background: 'rgba(255, 107, 53, 0.2)',
                            border: '1px solid #ff6b35',
                            color: '#ff6b35',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        ← Start Free Trial
                    </button>
                </div>

                <div style={{
                    textAlign: 'center',
                    marginBottom: '48px'
                }}>
                    <h2 style={{
                        color: '#f0f0f0',
                        fontSize: '1.8rem',
                        marginBottom: '16px',
                        fontWeight: '600'
                    }}>
                        Simple, Transparent Pricing
                    </h2>
                    <p style={{
                        fontSize: '1.1rem',
                        color: '#b0b0b0',
                        lineHeight: '1.6',
                        maxWidth: '600px',
                        margin: '0 auto'
                    }}>
                        Start with a free trial and choose the plan that works best for your family's budgeting needs.
                    </p>
                </div>

                {/* Pricing Cards */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(3, 1fr)',
                    gap: '24px',
                    marginBottom: '48px'
                }}>
                    {/* Free Trial */}
                    <div style={{
                        background: 'rgba(255, 107, 53, 0.1)',
                        border: '2px solid #ff6b35',
                        borderRadius: '12px',
                        padding: '32px 24px',
                        textAlign: 'center',
                        position: 'relative',
                        boxShadow: '0 4px 20px rgba(255, 107, 53, 0.2)',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                    }}>
                        <h3 style={{
                            color: '#ff6b35',
                            fontSize: '1.5rem',
                            marginBottom: '16px',
                            fontWeight: '700'
                        }}>
                            Free Trial
                        </h3>
                        <div style={{
                            marginBottom: '24px'
                        }}>
                            <span style={{
                                fontSize: '3rem',
                                fontWeight: '700',
                                color: '#f0f0f0'
                            }}>
                                $0
                            </span>
                            <span style={{
                                fontSize: '1.1rem',
                                color: '#b0b0b0',
                                marginLeft: '8px'
                            }}>
                                for 30 days
                            </span>
                        </div>
                        <div style={{ flex: 1 }}>
                            <ul style={{
                                listStyle: 'none',
                                padding: '0',
                                margin: '0 0 24px 0',
                                textAlign: 'left'
                            }}>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    Full access to all features
                                </li>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    Unlimited expense tracking
                                </li>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    Budget management & alerts
                                </li>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    CSV import/export
                                </li>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    Family member access
                                </li>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    No credit card required
                                </li>
                            </ul>
                        </div>
                        <button
                            onClick={() => navigate('/login?mode=register')}
                            style={{
                                width: '100%',
                                padding: '12px',
                                background: 'linear-gradient(135deg, #ff6b35, #ff8c42)',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                        >
                            Start Free Trial
                        </button>
                    </div>

                    {/* Monthly Plan */}
                    <div style={{
                        background: 'rgba(255, 107, 53, 0.1)',
                        border: '2px solid #ff6b35',
                        borderRadius: '12px',
                        padding: '32px 24px',
                        textAlign: 'center',
                        position: 'relative',
                        boxShadow: '0 4px 20px rgba(255, 107, 53, 0.2)',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                    }}>
                        <h3 style={{
                            color: '#ff6b35',
                            fontSize: '1.5rem',
                            marginBottom: '16px',
                            fontWeight: '700'
                        }}>
                            Monthly
                        </h3>
                        <div style={{
                            marginBottom: '24px'
                        }}>
                            <span style={{
                                fontSize: '3rem',
                                fontWeight: '700',
                                color: '#f0f0f0'
                            }}>
                                $8
                            </span>
                            <span style={{
                                fontSize: '1.1rem',
                                color: '#b0b0b0',
                                marginLeft: '8px'
                            }}>
                                per month
                            </span>
                        </div>
                        <div style={{ flex: 1 }}>
                            <ul style={{
                                listStyle: 'none',
                                padding: '0',
                                margin: '0 0 24px 0',
                                textAlign: 'left'
                            }}>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    All features included
                                </li>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    Priority customer support
                                </li>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    Regular feature updates
                                </li>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    Cancel anytime
                                </li>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    Secure data backup
                                </li>
                            </ul>
                        </div>
                        {/* <button style={{
                            width: '100%',
                            padding: '12px',
                            background: 'linear-gradient(135deg, #ff6b35, #ff8c42)',
                            border: 'none',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease'
                        }}
                            onMouseEnter={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                                e.target.style.borderColor = '#666';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                                e.target.style.borderColor = '#404040';
                            }}
                        >
                            Choose Monthly
                        </button> */}
                    </div>

                    {/* Annual Plan */}
                    <div style={{
                        background: 'rgba(46, 213, 115, 0.1)',
                        border: '2px solid #2ed573',
                        borderRadius: '12px',
                        padding: '32px 24px',
                        textAlign: 'center',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: '-12px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: '#2ed573',
                            color: 'white',
                            padding: '4px 16px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '600'
                        }}>
                            BEST VALUE
                        </div>
                        <h3 style={{
                            color: '#2ed573',
                            fontSize: '1.5rem',
                            marginBottom: '16px',
                            fontWeight: '700'
                        }}>
                            Annual
                        </h3>
                        <div style={{
                            marginBottom: '8px'
                        }}>
                            <span style={{
                                fontSize: '3rem',
                                fontWeight: '700',
                                color: '#f0f0f0'
                            }}>
                                $5
                            </span>
                            <span style={{
                                fontSize: '1.1rem',
                                color: '#b0b0b0',
                                marginLeft: '8px'
                            }}>
                                per month
                            </span>
                        </div>
                        <div style={{
                            marginBottom: '24px'
                        }}>
                            <span style={{
                                fontSize: '1rem',
                                color: '#2ed573',
                                fontWeight: '600'
                            }}>
                                $60 billed annually
                            </span>
                            <div style={{
                                fontSize: '14px',
                                color: '#b0b0b0',
                                marginTop: '4px'
                            }}>
                                Save $36 per year!
                            </div>
                        </div>
                        <div style={{ flex: 1 }}>
                            <ul style={{
                                listStyle: 'none',
                                padding: '0',
                                margin: '0 0 24px 0',
                                textAlign: 'left'
                            }}>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    All monthly features
                                </li>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    37% savings vs monthly
                                </li>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    Priority feature requests
                                </li>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    Extended data retention
                                </li>
                                <li style={{ padding: '8px 0', color: '#e0e0e0', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ color: '#2ed573', marginRight: '8px' }}>✓</span>
                                    Annual usage reports
                                </li>
                            </ul>
                        </div>
                        {/* <button style={{
                            width: '100%',
                            padding: '12px',
                            background: 'linear-gradient(135deg, #2ed573, #20bf6b)',
                            border: 'none',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease'
                        }}
                            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                        >
                            Choose Annual
                        </button> */}
                    </div>
                </div>

                {/* FAQ Section */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: '12px',
                    padding: '32px',
                    marginBottom: '32px'
                }}>
                    <h2 style={{
                        color: '#ff6b35',
                        fontSize: '1.8rem',
                        marginBottom: '24px',
                        textAlign: 'center'
                    }}>
                        Frequently Asked Questions
                    </h2>

                    <div style={{
                        textAlign: 'center',
                        background: 'rgba(255, 107, 53, 0.1)',
                        border: '1px solid rgba(255, 107, 53, 0.3)',
                        borderRadius: '12px',
                        padding: '32px'
                    }}>
                        <h2 style={{
                            color: '#f0f0f0',
                            fontSize: '1.5rem',
                            marginBottom: '16px'
                        }}>
                            How is this so cheap?
                        </h2>
                        <p style={{
                            color: '#b0b0b0',
                            marginBottom: '24px',
                            fontSize: '1.1rem'
                        }}>
                            I'm truly just trying to "keep the lights on" with this app. I originally made this for myself to track spending but then decided to see if others would be interested in using it.
                            I just need enough to cover hosting/service fees.  The profit margin is so low (especially on the yearly plan!), I'm just trying to break even.
                        </p>
                        <p style={{
                            color: '#b0b0b0',
                            marginBottom: '24px',
                            fontSize: '1.1rem'
                        }}>
                            When you are using this app, you're not supporting some large company with overhead fees and costs, you're supporting a developer that lives in Waterboro Maine who just enjoys making
                            helpful apps.
                        </p>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr',
                        gap: '24px'
                    }}>
                        <div>
                            <h3 style={{ color: '#f0f0f0', fontSize: '1.2rem', marginBottom: '8px' }}>
                                What happens after my free trial?
                            </h3>
                            <p style={{ color: '#b0b0b0', lineHeight: '1.6', margin: '0' }}>
                                After your 30-day free trial, you can choose to continue with either our monthly or annual plan. Your data will be preserved, and you'll have uninterrupted access to all features.
                            </p>
                        </div>

                        <div>
                            <h3 style={{ color: '#f0f0f0', fontSize: '1.2rem', marginBottom: '8px' }}>
                                Can I cancel anytime?
                            </h3>
                            <p style={{ color: '#b0b0b0', lineHeight: '1.6', margin: '0' }}>
                                Yes! You can cancel your subscription at any time. For monthly plans, you'll have access until the end of your billing period. Annual subscribers can request a prorated refund.
                            </p>
                        </div>

                        <div>
                            <h3 style={{ color: '#f0f0f0', fontSize: '1.2rem', marginBottom: '8px' }}>
                                Is my financial data secure?
                            </h3>
                            <p style={{ color: '#b0b0b0', lineHeight: '1.6', margin: '0' }}>
                                Absolutely. We use bank-level encryption to protect your data, and we never share your information with third parties. Your privacy and security are our top priorities.
                            </p>
                        </div>

                        <div>
                            <h3 style={{ color: '#f0f0f0', fontSize: '1.2rem', marginBottom: '8px' }}>
                                How many family members can use one account?
                            </h3>
                            <p style={{ color: '#b0b0b0', lineHeight: '1.6', margin: '0' }}>
                                All plans support unlimited family members. Each family member gets their own login credentials while sharing the same budget and expense data.
                            </p>
                        </div>

                        <div>
                            <h3 style={{ color: '#f0f0f0', fontSize: '1.2rem', marginBottom: '8px' }}>
                                Do you offer refunds?
                            </h3>
                            <p style={{ color: '#b0b0b0', lineHeight: '1.6', margin: '0' }}>
                                At this time, we don't offer refunds once you've committed to a monthly or yearly plan. Please make sure to utilize the 30-day trial to ensure the software does what you expect.
                            </p>
                        </div>

                        <div>
                            <h3 style={{ color: '#f0f0f0', fontSize: '1.2rem', marginBottom: '8px' }}>
                                What payment methods do you accept?
                            </h3>
                            <p style={{ color: '#b0b0b0', lineHeight: '1.6', margin: '0' }}>
                                We accept all major credit cards (Visa, MasterCard, American Express) via <strong>PayPal</strong>. All payments are processed securely through PayPal.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Contact Section */}
                <div style={{
                    textAlign: 'center',
                    background: 'rgba(255, 107, 53, 0.1)',
                    border: '1px solid rgba(255, 107, 53, 0.3)',
                    borderRadius: '12px',
                    padding: '32px'
                }}>
                    <h2 style={{
                        color: '#ff6b35',
                        fontSize: '1.5rem',
                        marginBottom: '16px'
                    }}>
                        Still Have Questions?
                    </h2>
                    <p style={{
                        color: '#b0b0b0',
                        marginBottom: '24px',
                        fontSize: '1.1rem'
                    }}>
                        Our support team is here to help you choose the right plan for your family.
                    </p>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '16px',
                        flexWrap: 'wrap'
                    }}>
                        <button
                            onClick={() => {
                                const subject = encodeURIComponent('Family Budget - Pricing Question');
                                const body = encodeURIComponent('Hi,\n\nI have a question about the Family Budget pricing plans.\n\nThanks!');
                                const mailtoLink = `mailto:info@duttonbiz.com?subject=${subject}&body=${body}`;

                                try {
                                    window.location.href = mailtoLink;
                                } catch (error) {
                                    // Fallback: copy email to clipboard and show alert
                                    navigator.clipboard.writeText('info@duttonbiz.com').then(() => {
                                        alert('Email address copied to clipboard: info@duttonbiz.com');
                                    }).catch(() => {
                                        alert('Please email us at: info@duttonbiz.com');
                                    });
                                }
                            }}
                            style={{
                                background: 'rgba(255, 107, 53, 0.2)',
                                border: '1px solid #ff6b35',
                                color: '#ff6b35',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 107, 53, 0.3)'}
                            onMouseLeave={(e) => e.target.style.background = 'rgba(255, 107, 53, 0.2)'}
                            title="Click to send an email to our support team"
                        >
                            📧 Contact Support
                        </button>
                        <button
                            onClick={() => navigate('/login?mode=register')}
                            style={{
                                background: 'linear-gradient(135deg, #ff6b35, #ff8c42)',
                                border: 'none',
                                color: 'white',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '600',
                                transition: 'transform 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                        >
                            🚀 Start Your Free Trial
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Pricing;